import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import useAuth from "@/lib/auth/useAuth";
import { useMemo } from "react";
import {
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { useFormContext } from "react-hook-form";
import { roundTo } from "@/lib/utils/numbers";
import useRequest from "@/lib/request/useRequest";
import { DSSATSimulation } from "@/types/dssat";
import dayjs from "dayjs";
import { sortArrayByKey } from "@/lib/utils/sorting";
import { Spinner } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { useI18n } from "@/lib/i18n/useI18n";
import { Resource } from "@/types/user";
import { CropCycleV2 } from "@/types/cropCycle";

function NUptakeTable() {
    const { t } = useTranslation("insights");
    const { formatDateMMDDYYYY } = useI18n();
    const { fieldId, selectedCropCycle } = useFieldDashboard() ?? {};
    const { useGetRequest } = useRequest();
    const { isAtLeastDetail } = useAuth();
    const f = useFormContext();
    const [startDate, endDate] = f.watch(["startDate", "endDate"]);

    const growthModelDetail = isAtLeastDetail(Resource.GROWTH_MODEL);
    const dssatSimulationsQuery = useGetRequest<DSSATSimulation[]>({
        url: `/fields/${fieldId}/dssat_simulations/`,
        enabled: !!fieldId,
    });
    const data = useMemo(() => {
        return sortArrayByKey(
            dssatSimulationsQuery.data?.filter((d) => {
                const date = dayjs(d.date);
                return (
                    d.crop_cycle === (selectedCropCycle as CropCycleV2).id &&
                    (date.isSame(startDate, "day") ||
                        date.isAfter(startDate)) &&
                    (date.isSame(endDate) || date.isBefore(endDate))
                );
            }) ?? [],
            "date",
        ).map((row) => ({
            ...row,
            date: dayjs(row.date).toDate(),
            [t("chartLabels.nitrogenApplied")]: row.nitrogen_applied,
            [t("chartLabels.nitrogenUptake")]: row.nitrogen_uptake,
            [t("chartLabels.nitrogenLeached")]: row.nitrogen_leached,
            [t("chartLabels.waterStress")]: growthModelDetail
                ? row.water_stress
                : undefined,
            [t("chartLabels.nitrogenStress")]: growthModelDetail
                ? row.nitrogen_stress
                : undefined,
            [t("chartLabels.leafNumber")]: growthModelDetail
                ? row.leaf_number
                : undefined,
            [t("chartLabels.canopyWeight")]: growthModelDetail
                ? row.canopy_weight
                : undefined,
            [t("chartLabels.rootDensity5cm")]: growthModelDetail
                ? row.root_density_5cm
                : undefined,
            [t("chartLabels.rootDensity15cm")]: growthModelDetail
                ? row.root_density_15cm
                : undefined,
            [t("chartLabels.rootDensity30cm")]: growthModelDetail
                ? row.root_density_30cm
                : undefined,
            [t("chartLabels.rootDensity60cm")]: growthModelDetail
                ? row.root_density_60cm
                : undefined,
            [t("chartLabels.rootDensity100cm")]: growthModelDetail
                ? row.root_density_100cm
                : undefined,
            [t("chartLabels.rootDensity200cm")]: growthModelDetail
                ? row.root_density_200cm
                : undefined,
            [t("chartLabels.soilMoisture5cm")]: growthModelDetail
                ? row.soil_moisutre_5cm
                : undefined,
            [t("chartLabels.soilMoisture15cm")]: growthModelDetail
                ? row.soil_moisutre_15cm
                : undefined,
            [t("chartLabels.soilMoisture30cm")]: growthModelDetail
                ? row.soil_moisutre_30cm
                : undefined,
            [t("chartLabels.soilMoisture60cm")]: growthModelDetail
                ? row.soil_moisutre_60cm
                : undefined,
            [t("chartLabels.soilMoisture100cm")]: growthModelDetail
                ? row.soil_moisutre_100cm
                : undefined,
            [t("chartLabels.soilMoisture200cm")]: growthModelDetail
                ? row.soil_moisutre_200cm
                : undefined,
            [t("chartLabels.ammonium5cm")]: growthModelDetail
                ? row.ammonium_5cm
                : undefined,
            [t("chartLabels.ammonium15cm")]: growthModelDetail
                ? row.ammonium_15cm
                : undefined,
            [t("chartLabels.ammonium30cm")]: growthModelDetail
                ? row.ammonium_30cm
                : undefined,
            [t("chartLabels.ammonium60cm")]: growthModelDetail
                ? row.ammonium_60cm
                : undefined,
            [t("chartLabels.ammonium100cm")]: growthModelDetail
                ? row.ammonium_100cm
                : undefined,
            [t("chartLabels.ammonium200cm")]: growthModelDetail
                ? row.ammonium_200cm
                : undefined,
            [t("chartLabels.nitrate5cm")]: growthModelDetail
                ? row.nitrate_5cm
                : undefined,
            [t("chartLabels.nitrate15cm")]: growthModelDetail
                ? row.nitrate_15cm
                : undefined,
            [t("chartLabels.nitrate30cm")]: growthModelDetail
                ? row.nitrate_30cm
                : undefined,
            [t("chartLabels.nitrate60cm")]: growthModelDetail
                ? row.nitrate_60cm
                : undefined,
            [t("chartLabels.nitrate100cm")]: growthModelDetail
                ? row.nitrate_100cm
                : undefined,
            [t("chartLabels.nitrate200cm")]: growthModelDetail
                ? row.nitrate_200cm
                : undefined,
        }));
    }, [
        dssatSimulationsQuery.data,
        startDate,
        endDate,
        t,
        growthModelDetail,
        selectedCropCycle,
    ]);

    const max = useMemo(() => {
        return Math.max(
            ...data.map((d) => Number(d.nitrogen_uptake)),
            ...data.map((d) => Number(d.nitrogen_applied)),
            ...data.map((d) => Number(d.nitrogen_leached)),
        );
    }, [data]);

    const maxRootDensity = useMemo(() => {
        if (!growthModelDetail) return 0;
        return (
            Math.max(
                ...data.map((d) => Number(d.root_density_5cm)),
                ...data.map((d) => Number(d.root_density_15cm)),
                ...data.map((d) => Number(d.root_density_30cm)),
                ...data.map((d) => Number(d.root_density_60cm)),
                ...data.map((d) => Number(d.root_density_100cm)),
                ...data.map((d) => Number(d.root_density_200cm)),
            ) + 1
        );
    }, [data, growthModelDetail]);

    const maxSoilMoisture = useMemo(() => {
        if (!growthModelDetail) return 0;
        return (
            Math.max(
                ...data
                    .filter((d) => d.soil_moisutre_5cm !== null)
                    .map((d) => Number(d.soil_moisutre_5cm)),
                ...data
                    .filter((d) => d.soil_moisutre_5cm !== null)
                    .map((d) => Number(d.soil_moisutre_15cm)),
                ...data
                    .filter((d) => d.soil_moisutre_5cm !== null)
                    .map((d) => Number(d.soil_moisutre_30cm)),
                ...data
                    .filter((d) => d.soil_moisutre_5cm !== null)
                    .map((d) => Number(d.soil_moisutre_60cm)),
                ...data
                    .filter((d) => d.soil_moisutre_5cm !== null)
                    .map((d) => Number(d.soil_moisutre_100cm)),
                ...data
                    .filter((d) => d.soil_moisutre_5cm !== null)
                    .map((d) => Number(d.soil_moisutre_200cm)),
            ) + 0.1
        );
    }, [data, growthModelDetail]);

    const maxAmmonium = useMemo(() => {
        if (!growthModelDetail) return 0;
        return (
            Math.max(
                ...data
                    .filter((d) => d.ammonium_5cm !== null)
                    .map((d) => Number(d.ammonium_5cm)),
                ...data
                    .filter((d) => d.ammonium_5cm !== null)
                    .map((d) => Number(d.ammonium_15cm)),
                ...data
                    .filter((d) => d.ammonium_5cm !== null)
                    .map((d) => Number(d.ammonium_30cm)),
                ...data
                    .filter((d) => d.ammonium_5cm !== null)
                    .map((d) => Number(d.ammonium_60cm)),
                ...data
                    .filter((d) => d.ammonium_5cm !== null)
                    .map((d) => Number(d.ammonium_100cm)),
                ...data
                    .filter((d) => d.ammonium_5cm !== null)
                    .map((d) => Number(d.ammonium_200cm)),
            ) + 0.1
        ).toFixed(2);
    }, [data, growthModelDetail]);

    const maxNitrate = useMemo(() => {
        if (!growthModelDetail) return 0;
        return (
            Math.max(
                ...data
                    .filter((d) => d.nitrate_5cm !== null)
                    .map((d) => Number(d.nitrate_5cm)),
                ...data
                    .filter((d) => d.nitrate_5cm !== null)
                    .map((d) => Number(d.nitrate_15cm)),
                ...data
                    .filter((d) => d.nitrate_5cm !== null)
                    .map((d) => Number(d.nitrate_30cm)),
                ...data
                    .filter((d) => d.nitrate_5cm !== null)
                    .map((d) => Number(d.nitrate_60cm)),
                ...data
                    .filter((d) => d.nitrate_5cm !== null)
                    .map((d) => Number(d.nitrate_100cm)),
                ...data
                    .filter((d) => d.nitrate_5cm !== null)
                    .map((d) => Number(d.nitrate_200cm)),
            ) + 0.1
        );
    }, [data, growthModelDetail]);

    return (
        <div className="flex flex-col gap-4">
            <div className="text-lg font-bold">
                {t("chartLabels.nitrogenDynamics")}
            </div>
            <ResponsiveContainer width="100%" height={400}>
                {dssatSimulationsQuery.isLoading ? (
                    <Spinner label={t("messages.loadingData")} />
                ) : !data.length ? (
                    <div className="bg-gray-100 h-full rounded-sm">
                        <div className=" p-3 italic text-center">
                            {t("messages.noDataSelectedDates")}
                        </div>
                    </div>
                ) : (
                    <LineChart data={data} margin={{ bottom: 16, left: 8 }}>
                        <XAxis
                            dataKey="date"
                            scale="time"
                            spacing={50}
                            angle={-8}
                            tickFormatter={(t) => formatDateMMDDYYYY(t)}
                        />
                        <YAxis
                            label={{
                                value: t("chartLabels.nitrogenLbNAc"),
                                angle: -90,
                                position: "left",
                                dx: 10,
                            }}
                            domain={[0, max]}
                        />
                        <Tooltip
                            labelFormatter={(t) => formatDateMMDDYYYY(t)}
                            formatter={(value, name) => [
                                `${roundTo(value as number, 1)}`,
                                name,
                            ]}
                        />
                        <Legend />

                        <Line
                            dataKey={t("chartLabels.nitrogenUptake")}
                            stroke="#FF0000"
                            dot={false}
                        />
                        <Line
                            dataKey={t("chartLabels.nitrogenApplied")}
                            stroke="#00FF00"
                            dot={false}
                        />
                        <Line
                            dataKey={t("chartLabels.nitrogenLeached")}
                            stroke="#0000FF"
                            dot={false}
                        />
                        {growthModelDetail && (
                            <Line
                                dataKey={t("chartLabels.nitrogenStress")}
                                stroke="#FFA500"
                                dot={false}
                            />
                        )}
                    </LineChart>
                )}
            </ResponsiveContainer>

            {growthModelDetail && (
                <>
                    <div className="text-lg font-bold">
                        {t("chartLabels.rootDensity")}
                    </div>
                    <ResponsiveContainer width="100%" height={400}>
                        {dssatSimulationsQuery.isLoading ? (
                            <Spinner label={t("messages.loadingData")} />
                        ) : !data.length ? (
                            <div className="bg-gray-100 h-full rounded-sm">
                                <div className=" p-3 italic text-center">
                                    {t("messages.noDataSelectedDates")}
                                </div>
                            </div>
                        ) : (
                            <LineChart
                                data={data}
                                margin={{ bottom: 16, left: 8 }}
                            >
                                <XAxis
                                    dataKey="date"
                                    scale="time"
                                    spacing={50}
                                    angle={-8}
                                    tickFormatter={(t) =>
                                        dayjs(t).format("MM/DD/YY")
                                    }
                                />
                                <YAxis
                                    label={{
                                        value: t(
                                            "chartLabels.rootDensitycm-cm3",
                                        ),
                                        angle: -90,
                                        position: "left",
                                        dx: 10,
                                    }}
                                    domain={[0, maxRootDensity]}
                                />
                                <Tooltip
                                    labelFormatter={(t) =>
                                        dayjs(t).format("MM/DD/YY")
                                    }
                                    formatter={(value, name) => [
                                        `${roundTo(value as number, 1)}`,
                                        name,
                                    ]}
                                />
                                <Legend />
                                <Line
                                    dataKey={t("chartLabels.rootDensity5cm")}
                                    stroke="#8B4513"
                                    dot={false}
                                />
                                <Line
                                    dataKey={t("chartLabels.rootDensity15cm")}
                                    stroke="#A0522D"
                                    dot={false}
                                />
                                <Line
                                    dataKey={t("chartLabels.rootDensity30cm")}
                                    stroke="#D2691E"
                                    dot={false}
                                />
                                <Line
                                    dataKey={t("chartLabels.rootDensity60cm")}
                                    stroke="#FF7F50"
                                    dot={false}
                                />
                                <Line
                                    dataKey={t("chartLabels.rootDensity100cm")}
                                    stroke="#FF8C00"
                                    dot={false}
                                />
                                <Line
                                    dataKey={t("chartLabels.rootDensity200cm")}
                                    stroke="#FFA500"
                                    dot={false}
                                />
                            </LineChart>
                        )}
                    </ResponsiveContainer>

                    <div className="text-lg font-bold">
                        {t("chartLabels.soilMoisture")}
                    </div>
                    <ResponsiveContainer width="100%" height={400}>
                        {dssatSimulationsQuery.isLoading ? (
                            <Spinner label={t("messages.loadingData")} />
                        ) : !data.length ? (
                            <div className="bg-gray-100 h-full rounded-sm">
                                <div className=" p-3 italic text-center">
                                    {t("messages.noDataSelectedDates")}
                                </div>
                            </div>
                        ) : (
                            <LineChart
                                data={data}
                                margin={{ bottom: 16, left: 8 }}
                            >
                                <XAxis
                                    dataKey="date"
                                    scale="time"
                                    spacing={50}
                                    angle={-8}
                                    tickFormatter={(t) =>
                                        dayjs(t).format("MM/DD/YY")
                                    }
                                />
                                <YAxis
                                    label={{
                                        value: t(
                                            "chartLabels.soilMoisturecm3-cm3",
                                        ),
                                        angle: -90,
                                        position: "left",
                                        dx: 10,
                                    }}
                                    domain={[0, maxSoilMoisture]}
                                />
                                <Tooltip
                                    labelFormatter={(t) =>
                                        dayjs(t).format("MM/DD/YY")
                                    }
                                    formatter={(value, name) => [
                                        `${roundTo(value as number, 1)}`,
                                        name,
                                    ]}
                                />
                                <Legend />
                                <Line
                                    dataKey={t("chartLabels.soilMoisture5cm")}
                                    stroke="#8B4513"
                                    dot={false}
                                />
                                <Line
                                    dataKey={t("chartLabels.soilMoisture15cm")}
                                    stroke="#A0522D"
                                    dot={false}
                                />
                                <Line
                                    dataKey={t("chartLabels.soilMoisture30cm")}
                                    stroke="#D2691E"
                                    dot={false}
                                />
                                <Line
                                    dataKey={t("chartLabels.soilMoisture60cm")}
                                    stroke="#FF7F50"
                                    dot={false}
                                />
                                <Line
                                    dataKey={t("chartLabels.soilMoisture100cm")}
                                    stroke="#FF8C00"
                                    dot={false}
                                />
                                <Line
                                    dataKey={t("chartLabels.soilMoisture200cm")}
                                    stroke="#FFA500"
                                    dot={false}
                                />
                            </LineChart>
                        )}
                    </ResponsiveContainer>

                    <div className="text-lg font-bold">
                        {t("chartLabels.ammonium")}
                    </div>
                    <ResponsiveContainer width="100%" height={400}>
                        {dssatSimulationsQuery.isLoading ? (
                            <Spinner label={t("messages.loadingData")} />
                        ) : !data.length ? (
                            <div className="bg-gray-100 h-full rounded-sm">
                                <div className=" p-3 italic text-center">
                                    {t("messages.noDataSelectedDates")}
                                </div>
                            </div>
                        ) : (
                            <LineChart
                                data={data}
                                margin={{ bottom: 16, left: 8 }}
                            >
                                <XAxis
                                    dataKey="date"
                                    scale="time"
                                    spacing={50}
                                    angle={-8}
                                    tickFormatter={(t) =>
                                        dayjs(t).format("MM/DD/YY")
                                    }
                                />
                                <YAxis
                                    label={{
                                        value: t("chartLabels.ammoniumPpm"),
                                        angle: -90,
                                        position: "left",
                                        dx: 10,
                                    }}
                                    domain={[0, maxAmmonium]}
                                />
                                <Tooltip
                                    labelFormatter={(t) =>
                                        dayjs(t).format("MM/DD/YY")
                                    }
                                    formatter={(value, name) => [
                                        `${roundTo(value as number, 1)}`,
                                        name,
                                    ]}
                                />
                                <Legend />
                                <Line
                                    dataKey={t("chartLabels.ammonium5cm")}
                                    stroke="#8B4513"
                                    dot={false}
                                />
                                <Line
                                    dataKey={t("chartLabels.ammonium15cm")}
                                    stroke="#A0522D"
                                    dot={false}
                                />
                                <Line
                                    dataKey={t("chartLabels.ammonium30cm")}
                                    stroke="#D2691E"
                                    dot={false}
                                />
                                <Line
                                    dataKey={t("chartLabels.ammonium60cm")}
                                    stroke="#FF7F50"
                                    dot={false}
                                />
                                <Line
                                    dataKey={t("chartLabels.ammonium100cm")}
                                    stroke="#FF8C00"
                                    dot={false}
                                />
                                <Line
                                    dataKey={t("chartLabels.ammonium200cm")}
                                    stroke="#FFA500"
                                    dot={false}
                                />
                            </LineChart>
                        )}
                    </ResponsiveContainer>

                    <div className="text-lg font-bold">
                        {t("chartLabels.nitrate")}
                    </div>
                    <ResponsiveContainer width="100%" height={400}>
                        {dssatSimulationsQuery.isLoading ? (
                            <Spinner label={t("messages.loadingData")} />
                        ) : !data.length ? (
                            <div className="bg-gray-100 h-full rounded-sm">
                                <div className=" p-3 italic text-center">
                                    {t("messages.noDataSelectedDates")}
                                </div>
                            </div>
                        ) : (
                            <LineChart
                                data={data}
                                margin={{ bottom: 16, left: 8 }}
                            >
                                <XAxis
                                    dataKey="date"
                                    scale="time"
                                    spacing={50}
                                    angle={-8}
                                    tickFormatter={(t) =>
                                        dayjs(t).format("MM/DD/YY")
                                    }
                                />
                                <YAxis
                                    label={{
                                        value: t("chartLabels.nitratePpm"),
                                        angle: -90,
                                        position: "left",
                                        dx: 10,
                                    }}
                                    domain={[0, maxNitrate]}
                                />
                                <Tooltip
                                    labelFormatter={(t) =>
                                        dayjs(t).format("MM/DD/YY")
                                    }
                                    formatter={(value, name) => [
                                        `${roundTo(value as number, 1)}`,
                                        name,
                                    ]}
                                />
                                <Legend />
                                <Line
                                    dataKey={t("chartLabels.nitrate5cm")}
                                    stroke="#8B4513"
                                    dot={false}
                                />
                                <Line
                                    dataKey={t("chartLabels.nitrate15cm")}
                                    stroke="#A0522D"
                                    dot={false}
                                />
                                <Line
                                    dataKey={t("chartLabels.nitrate30cm")}
                                    stroke="#D2691E"
                                    dot={false}
                                />
                                <Line
                                    dataKey={t("chartLabels.nitrate60cm")}
                                    stroke="#FF7F50"
                                    dot={false}
                                />
                                <Line
                                    dataKey={t("chartLabels.nitrate100cm")}
                                    stroke="#FF8C00"
                                    dot={false}
                                />
                                <Line
                                    dataKey={t("chartLabels.nitrate200cm")}
                                    stroke="#FFA500"
                                    dot={false}
                                />
                            </LineChart>
                        )}
                    </ResponsiveContainer>
                </>
            )}
        </div>
    );
}

export default NUptakeTable;
